import { useMemo, useState } from 'react';
import { InformationCircleIcon as InformationCircleIconOutline } from '@heroicons/react/24/outline';
import { BanknotesIcon, CurrencyDollarIcon, InformationCircleIcon, StarIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

import { CARRIER_SHIP_FEE_REDUCTION } from 'constants/plusMembership';
import { ALCOHOL_SERVICE_FEE, MAX_CREDITS_PER_ORDER, MIN_WINE_BOTTLE_QTY_TO_SHIP } from 'constants/shop';
import { JoinModal } from 'features/paid-membership/JoinModal';
import CartDiscountCodeForm from 'features/shop/cart/CartDiscountCodeForm';
import ColdPackFeeModal from 'features/shop/cart/ColdPackFeeModal';
import CreditsInfoModal from 'features/shop/cart/CreditsInfoModal';
import DiscountInfoModal from 'features/shop/cart/DiscountInfoModal';
import EBTPaymentInfoModal from 'features/shop/cart/EBTPaymentInfoModal';
import FrozenPackagingModal from 'features/shop/cart/FrozenPackagingModal';
import {
    PlusMembershipMemberBanner,
    PlusMembershipProspectBanner,
    PlusMembershipSavingsBanner,
} from 'features/shop/cart/plusMembershipBanners';
import { TaxAndFeesModal } from 'features/shop/cart/TaxAndFeesModal';
import Button from 'honeydew/Button/Button';
import Tag from 'honeydew/Tag';
import useOrderMin from 'hooks/useOrderMin';
import DriverTipsInfoModal from 'shared/driver-tips/DriverTipsInfoModal';
import EditDriverTipModal from 'shared/driver-tips/EditDriverTipModal';
import { trackEvent } from 'utils/analyticsUtils';
import {
    filterPerkSelections,
    getColdPackItems,
    getFrozenItems,
    getGroceryItems,
    getShowOperationsFee,
    getTotalWineBottleQty,
} from 'utils/orderUtils';
import {
    getCartAlcoholDiscount,
    getCartAlcoholServiceFee,
    getCartAppliedCredits,
    getCartAppliedDiscounts,
    getCartEBTSubtotal,
    getCartEstimatedTax,
    getCartEstimatedTaxAndFees,
    getCartNonMemberSavings,
    getCartShippingFee,
    getCartSnapEligibleSubtotals,
    getCartSubtotal,
    getCartTotal,
    getFreeShippingThresholdMet,
    getUnmetReferralDiscount,
    getWaiveColdPackFee,
    getWaiveFrozenFee,
    PriceType,
} from 'utils/priceUtils';
import twclsx from 'utils/twclsx';

/**
 * CartPrices component displays the pricing breakdown for a shopping cart including
 * subtotal, shipping, fees, discounts, credits, and total. It also handles Plus
 * membership pricing and displays relevant savings and promotional information.
 *
 * @param {Object} props - Component props
 * @param {Object} props.account - User account information including loyalty status, order details, and feature flags
 * @param {boolean} [props.account.loyaltyEligible] - Whether user is eligible for loyalty points
 * @param {number} [props.account.preferredTipVal] - User's default driver tip amount
 * @param {number} [props.account.shippingFee] - Base shipping fee for the account
 * @param {boolean} [props.account.isDeliveredByImperfect] - Whether delivery is handled by Imperfect
 * @param {Object} [props.account.nextOrder] - Details about the user's next order
 * @param {string} [props.account.nextOrder.chargeID] - Unique identifier for the charge
 * @param {Object} [props.account.nextOrder.cartExperience] - Cart experience settings including fees
 * @param {number} [props.account.nextOrder.cartExperience.coldKeeperFee] - Fee for cold pack packaging
 * @param {number} [props.account.nextOrder.cartExperience.frozenFee] - Fee for frozen item packaging
 * @param {number} [props.account.nextOrder.cartExperience.operationsFee] - Operations fee amount
 * @param {boolean} [props.account.nextOrder.cartExperience.freeShippingEligible] - Free shipping eligibility
 * @param {number} [props.account.nextOrder.cartExperience.freeShippingThreshold] - Threshold for free shipping
 * @param {Object} [props.account.featureFlags] - Feature flags for the account
 * @param {boolean} [props.account.featureFlags.featureCanAddDriverTips] - Whether driver tips feature is enabled
 * @param {Object} props.credits - Credit information for the user
 * @param {number} props.credits.sumCreditBalance - Total available credit balance
 * @param {Object} props.discounts - Available and applied discount information
 * @param {boolean} [props.plusMembershipEligibleOrder=false] - Whether the order is eligible for Plus membership pricing
 * @param {boolean} [props.isMPlusInYourFace=false] - Amplitude `m-plus-in-your-face` experiment: inline savings banner in cart prices
 * @param {number} [props.plusMembershipShippingFee] - Shipping fee with Plus membership applied
 * @param {string} [props.plusMembershipShortBranding] - Short branding text for Plus membership (e.g., "Plus+")
 * @param {Object} [props.plusMembershipStaticBannerRef] - Ref for Plus membership banner intersection observer
 * @param {Object} [props.plusMembershipSavingsBannerRef] - Ref for Plus membership savings banner intersection observer
 * @param {boolean} [props.plusMembershipSavingsBannerInView] - Whether the M+ cart savings banner intersects the viewport
 * @param {Object} props.selections - Selected items in the cart
 * @param {Array<Object>} [props.selections.selected] - Array of selected product items
 * @param {Array<Object>} [props.selections.selectedBundles] - Array of selected bundle items
 * @param {number} [props.selections.chargeTipAmount] - Tip amount set for this specific order
 * @param {number} [props.selections.estimatedLoyaltyPoints] - Estimated loyalty points to be earned
 * @param {number} [props.selections.estimatedTax] - Estimated tax amount
 * @param {number} [props.selections.shipping] - Shipping amount
 * @param {number} [props.selections.alcoholDiscountPercentage] - Percentage discount on alcohol items
 * @param {Object} props.subscription - User subscription information
 * @param {Object} [props.subscription.ebtCard] - EBT card information if applicable
 * @param {Object} [props.wineOrderCardRef] - Ref to wine order card element for scrolling
 * @param {Element} [props.wineOrderCardRef.current] - Current DOM element reference
 *
 * @returns {JSX.Element} The rendered cart pricing breakdown component
 */
function CartPrices({
    account,
    credits,
    discounts,
    isMPlusInYourFace = false,
    plusMembershipEligibleOrder,
    plusMembershipSavingsBannerInView,
    plusMembershipSavingsBannerRef,
    plusMembershipShippingFee,
    plusMembershipShortBranding,
    plusMembershipStaticBannerRef,
    selections,
    subscription,
    wineOrderCardRef,
}) {
    const [driverTipsModalOpen, setDriverTipsModalOpen] = useState(false);
    const [editDriverTipsModalOpen, setEditDriverTipsModalOpen] = useState(false);
    const [taxAndFeesModalOpen, setTaxAndFeesModalOpen] = useState(false);
    const [frozenPackagingModalOpen, setFrozenPackagingModalOpen] = useState(false);
    const [creditsInfoModalOpen, setCreditsInfoModalOpen] = useState(false);
    const [ebtPaymentInfoModalOpen, setEBTPaymentInfoModalOpen] = useState(false);
    const [coldPackFeeModalOpen, setColdPackFeeModalOpen] = useState(false);
    const [plusMembershipJoinModalOpen, setPlusMembershipJoinModalOpen] = useState(false);

    const [discountInfoModalOpen, setDiscountInfoModalOpen] = useState(false);
    const [discountInfoModalDiscount, setDiscountInfoModalDiscount] = useState(null);

    const { loyaltyEligible, preferredTipVal, shippingFee: baseShippingFee } = account;
    const { chargeID } = account?.nextOrder ?? {};
    const {
        coldKeeperFee,
        frozenFee,
        frozenFeeWaiveThreshold,
        operationsFee = 0,
    } = account?.nextOrder?.cartExperience ?? {};
    const { featureCanAddDriverTips } = account?.featureFlags ?? {};
    const { sumCreditBalance } = credits;
    const { alcoholDiscountPercentage } = selections;

    const filteredSelections = filterPerkSelections(selections);
    const groceryItems = getGroceryItems(selections);
    const coldPackItems = getColdPackItems(filteredSelections);
    const frozenItems = getFrozenItems(filteredSelections);
    const totalWineBottleQty = getTotalWineBottleQty(filteredSelections);

    const { chargeTipAmount, estimatedLoyaltyPoints } = filteredSelections;
    const displayOneTimeOrderTip = chargeTipAmount !== preferredTipVal;
    const orderTip = displayOneTimeOrderTip ? chargeTipAmount : preferredTipVal;
    const tipAdded = orderTip > 0;

    const generateTipCopy = () => {
        if (displayOneTimeOrderTip && preferredTipVal === 0) return 'One-Time';

        if (displayOneTimeOrderTip && tipAdded) return 'Updated';

        if (!displayOneTimeOrderTip && tipAdded) return 'Default';

        return '';
    };

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const { isOrderMinMet } = useOrderMin();

    const subtotal = getCartSubtotal({
        account,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        selections,
    });

    const subtotalWithoutPlusMembership = getCartSubtotal({
        account,
        options: {
            priceType: PriceType.DEFAULT,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        selections,
    });

    const estimatedTax = getCartEstimatedTax({
        account,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        selections,
    });

    const estimatedTaxAndFees = getCartEstimatedTaxAndFees({
        account,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        selections,
    });

    const waiveFrozenFee = getWaiveFrozenFee({
        account,
        options: { priceType },
        selections,
    });

    const waiveColdPackFee = getWaiveColdPackFee({
        account,
        options: { priceType },
        selections,
    });

    const freeShippingThresholdMet = getFreeShippingThresholdMet({
        account,
        options: { priceType },
        selections,
    });

    const cartShippingFee = getCartShippingFee({
        account,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        plusMembership: { plusMembershipShippingFee },
        selections,
    });

    const { appliedDiscounts } = getCartAppliedDiscounts({
        account,
        discounts,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        selections,
    });

    const cartAppliedCredits = getCartAppliedCredits({
        account,
        credits,
        discounts,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        plusMembership: { plusMembershipShippingFee },
        selections,
    });

    const ebtSubtotal = getCartEBTSubtotal({ selections });

    const total = getCartTotal({
        account,
        credits,
        discounts,
        options: {
            priceType,
            ignoreOrderMin: !!subscription?.ebtCard,
            isOrderMinMet,
        },
        plusMembership: { plusMembershipShippingFee },
        selections,
    });

    const { nonSnapEligibleSubtotal, snapEligibleSubtotal } = getCartSnapEligibleSubtotals({ selections });
    const alcoholServiceFee = getCartAlcoholServiceFee({ selections });
    const alcoholDiscount = getCartAlcoholDiscount({ selections });
    const cartNonMemberSavings = getCartNonMemberSavings({ account, selections });

    const unmetReferralDiscount = getUnmetReferralDiscount({
        account,
        discounts,
        options: { priceType },
        selections,
    });

    const freeShippingWithMembership =
        plusMembershipEligibleOrder && !freeShippingThresholdMet && plusMembershipShippingFee === 0;

    const shippingDiscountWithMembership =
        plusMembershipEligibleOrder &&
        !freeShippingThresholdMet &&
        plusMembershipShippingFee > 0 &&
        plusMembershipShippingFee < baseShippingFee;

    const handleClickWaveAlcoholFee = () => {
        wineOrderCardRef?.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const showOperationsFee = getShowOperationsFee({ cartExperience: account?.nextOrder?.cartExperience });

    const hasPlusMemberSelection = useMemo(
        () => selections?.selected?.some((selection) => selection.plusMemberPriceEligible),
        [selections]
    );

    const plusMembershipShippingBenefitCopy = account?.isDeliveredByImperfect
        ? 'Never pay shipping with'
        : `Get $${CARRIER_SHIP_FEE_REDUCTION} off shipping per order with`;

    const handlePlusMembershipShippingBenefitClick = () => {
        trackEvent('shipping savings in cart click');
        setPlusMembershipJoinModalOpen(true);
    };

    return (
        <div className="mb-10 bg-white px-15 py-10">
            <CartDiscountCodeForm />
            {subscription?.ebtCard ? (
                <>
                    <div className="mt-10 flex justify-between">
                        <p className="text-body-sm">SNAP-Eligible Items</p>
                        <p className="text-body-sm">${snapEligibleSubtotal.toFixed(2)}</p>
                    </div>
                    <div className="mt-5 flex justify-between">
                        <p className="text-body-sm">Non-SNAP-Eligible Items</p>
                        <p className="text-body-sm">${nonSnapEligibleSubtotal.toFixed(2)}</p>
                    </div>
                </>
            ) : (
                <div className="mt-10 flex justify-between" data-testid="subtotal">
                    <p className="text-body-sm">Subtotal</p>
                    {plusMembershipEligibleOrder && subtotalWithoutPlusMembership > subtotal ? (
                        <span className="flex items-baseline gap-5">
                            <p className="text-body-sm line-through">${subtotalWithoutPlusMembership.toFixed(2)}</p>
                            <p className="text-body-sm font-grotesk-bold text-cranberry">
                                ${subtotal.toFixed(2)} with {plusMembershipShortBranding}
                            </p>
                        </span>
                    ) : (
                        <p className="text-body-sm">${subtotal.toFixed(2)}</p>
                    )}
                </div>
            )}
            {groceryItems.length > 0 && !!(isOrderMinMet || subscription?.ebtCard) && (
                <div className="mt-5 flex items-baseline justify-between" data-testid="shipping-fee">
                    <div>
                        <p className="text-body-sm">Shipping</p>
                        {!plusMembershipEligibleOrder && !freeShippingThresholdMet && hasPlusMemberSelection && (
                            <button
                                className="text-body-sm text-left text-cranberry hover:opacity-80"
                                onClick={handlePlusMembershipShippingBenefitClick}
                                type="button"
                            >
                                <span className="underline">{plusMembershipShippingBenefitCopy}</span>{' '}
                                <Tag bgColor="cranberry" variant="dark">
                                    {plusMembershipShortBranding}
                                </Tag>
                            </button>
                        )}
                    </div>
                    {!freeShippingWithMembership && cartShippingFee <= 0 && (
                        <div className="flex items-baseline gap-5">
                            <p className="text-body-sm line-through">${baseShippingFee?.toFixed(2)}</p>
                            <p className="text-body-sm text-brand-primary font-grotesk-bold">FREE</p>
                        </div>
                    )}
                    {freeShippingWithMembership && (
                        <div className="flex items-baseline gap-5">
                            <p className="text-body-sm line-through">${baseShippingFee?.toFixed(2)}</p>
                            <p className="text-body-sm font-grotesk-bold text-cranberry">
                                FREE with {plusMembershipShortBranding}
                            </p>
                        </div>
                    )}
                    {!shippingDiscountWithMembership && cartShippingFee > 0 && (
                        <p className="text-body-sm">${cartShippingFee.toFixed(2)}</p>
                    )}
                    {shippingDiscountWithMembership && (
                        <div className="flex items-baseline gap-5">
                            <p className="text-body-sm line-through">${baseShippingFee.toFixed(2)}</p>
                            <p className="text-body-sm font-grotesk-bold text-cranberry">
                                ${plusMembershipShippingFee.toFixed(2)} with {plusMembershipShortBranding}
                            </p>
                        </div>
                    )}
                </div>
            )}
            {groceryItems.length > 0 && !!(isOrderMinMet || subscription?.ebtCard) && frozenItems.length > 0 && (
                <div className="mt-5 flex items-baseline justify-between" data-testid="frozen-packaging-fee">
                    {waiveFrozenFee ? (
                        <>
                            <p className="text-body-sm">Frozen Packaging</p>
                            <p className="text-body-sm text-brand-primary font-grotesk-bold">WAIVED</p>
                        </>
                    ) : (
                        <>
                            <div
                                className={twclsx(
                                    'flex items-center gap-5',
                                    frozenFeeWaiveThreshold != null && 'items-baseline'
                                )}
                            >
                                <p className="text-body-sm">Frozen Packaging</p>
                                {frozenFeeWaiveThreshold == null ? (
                                    <button
                                        aria-label="Frozen packaging information"
                                        onClick={() => setFrozenPackagingModalOpen(true)}
                                        type="button"
                                    >
                                        <InformationCircleIconOutline className="mb-[2px]" width={18} />
                                    </button>
                                ) : (
                                    <Button
                                        className="link text-body-sm"
                                        onClick={() => setFrozenPackagingModalOpen(true)}
                                    >
                                        Waive
                                    </Button>
                                )}
                            </div>
                            <p className="text-body-sm">${frozenFee?.toFixed(2)}</p>
                        </>
                    )}
                    {frozenItems.length > 0 && (
                        <FrozenPackagingModal
                            frozenFee={frozenFee}
                            frozenFeeWaiveThreshold={frozenFeeWaiveThreshold}
                            handleClose={() => setFrozenPackagingModalOpen(false)}
                            open={frozenPackagingModalOpen}
                        />
                    )}
                </div>
            )}
            {coldKeeperFee > 0 && coldPackItems.length > 0 && (
                <div className="mt-5 flex justify-between" data-testid="cold-pack-fee">
                    {waiveColdPackFee ? (
                        <>
                            <p className="text-body-sm">Cold Pack Fee</p>
                            <div className="flex items-baseline gap-5">
                                <p className="text-body-sm line-through">${coldKeeperFee.toFixed(2)}</p>
                                <p className="text-body-sm text-brand-primary font-grotesk-bold">WAIVED</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-baseline gap-5">
                                <p className="text-body-sm">Cold Pack Fee</p>
                                <Button className="link text-body-sm" onClick={() => setColdPackFeeModalOpen(true)}>
                                    Waive
                                </Button>
                                <ColdPackFeeModal
                                    coldPackFee={coldKeeperFee}
                                    handleClose={() => setColdPackFeeModalOpen(false)}
                                    open={coldPackFeeModalOpen}
                                />
                            </div>
                            <p className="text-body-sm">${coldKeeperFee.toFixed(2)}</p>
                        </>
                    )}
                </div>
            )}
            {totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_TO_SHIP && (
                <div className="mt-5 flex justify-between" data-testid="alcohol-service-fee">
                    {alcoholServiceFee <= 0 ? (
                        <>
                            <p className="text-body-sm">Alcohol Service Fee</p>
                            <div className="flex items-baseline gap-5">
                                <p className="text-body-sm line-through">${ALCOHOL_SERVICE_FEE.toFixed(2)}</p>
                                <p className="text-body-sm text-brand-primary font-grotesk-bold">WAIVED</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-baseline gap-5">
                                <p className="text-body-sm">Alcohol Service Fee</p>
                                <Button className="link text-body-sm" onClick={handleClickWaveAlcoholFee}>
                                    Waive
                                </Button>
                            </div>
                            <p className="text-body-sm">${alcoholServiceFee.toFixed(2)}</p>
                        </>
                    )}
                </div>
            )}
            {appliedDiscounts.map((discount, index) => (
                <div
                    className="mt-5 flex justify-between"
                    data-testid={`applied-discounts-${index}`}
                    key={discount.discountCode}
                >
                    <div className="flex items-center gap-5">
                        <p className="text-body-sm flex items-center gap-5">
                            Discount <span className="text-brand-primary">({discount.discountCode})</span>
                        </p>
                        <button
                            aria-label="Discount information"
                            onClick={() => {
                                setDiscountInfoModalDiscount(discount);
                                setDiscountInfoModalOpen(true);
                            }}
                            type="button"
                        >
                            <InformationCircleIconOutline className="mb-[2px]" width={18} />
                        </button>
                    </div>
                    <p className="text-body-sm text-brand-primary">-${discount.dollarValue.toFixed(2)}</p>
                </div>
            ))}
            {alcoholDiscount > 0 && (
                <div className="mt-5 flex justify-between" data-testid="alcohol-discount">
                    <p className="text-body-sm">Alcohol Savings (-{alcoholDiscountPercentage}%)</p>
                    <p className="text-body-sm">-${alcoholDiscount.toFixed(2)}</p>
                </div>
            )}
            <div className="mt-5 flex justify-between" data-testid="estimated-tax">
                <div className="flex items-center gap-5">
                    <p className="text-body-sm">{showOperationsFee ? 'Estimated Tax & Fees' : 'Estimated Tax'}</p>
                    <button
                        aria-label="Estimated tax and fees information"
                        onClick={() => setTaxAndFeesModalOpen(true)}
                        type="button"
                    >
                        <InformationCircleIconOutline className="mb-[2px]" width={18} />
                    </button>
                </div>
                <p className="text-body-sm">
                    ${showOperationsFee ? estimatedTaxAndFees.toFixed(2) : estimatedTax.toFixed(2)}
                </p>
            </div>
            {cartAppliedCredits > 0 && !!(isOrderMinMet || subscription?.ebtCard) && (
                <div className="mt-5 flex justify-between" data-testid="credits">
                    <div className="flex items-center gap-5">
                        <p className="text-body-sm text-brand-primary flex items-center gap-5">Credits</p>
                        <button
                            aria-label="Credits information"
                            onClick={() => setCreditsInfoModalOpen(true)}
                            type="button"
                        >
                            <InformationCircleIconOutline className="mb-[2px]" width={18} />
                        </button>
                    </div>
                    <p className="text-body-sm text-brand-primary">-${cartAppliedCredits.toFixed(2)}</p>
                </div>
            )}
            {ebtSubtotal > 0 && (
                <div className="mt-5 flex justify-between" data-testid="credits">
                    <div className="flex items-center gap-5">
                        <p className="text-body-sm text-brand-primary flex items-center gap-5">EBT Payment</p>
                        <button
                            aria-label="EBT payment amount"
                            onClick={() => setEBTPaymentInfoModalOpen(true)}
                            type="button"
                        >
                            <InformationCircleIconOutline className="mb-[2px]" width={18} />
                        </button>
                    </div>
                    <p className="text-body-sm text-brand-primary">-${ebtSubtotal.toFixed(2)}</p>
                </div>
            )}
            {featureCanAddDriverTips && (
                <div className="bg-malt-off-white -mx-15 mt-5 px-15 py-10">
                    <div className="flex justify-between">
                        <p className={clsx('flex', { 'text-body-xs': !tipAdded, 'text-body-sm': tipAdded })}>
                            <span className="notranslate font-grotesk-bold">
                                {generateTipCopy()} Driver Tip
                                {tipAdded && <span> (optional)</span>}
                            </span>
                            <button
                                type="button"
                                className="link ml-10"
                                onClick={() => {
                                    setEditDriverTipsModalOpen(true);
                                }}
                            >
                                {tipAdded
                                    ? 'Edit'
                                    : `Add a ${preferredTipVal === 0 ? 'one-time ' : ''}tip for your driver`}
                            </button>
                            <button type="button" onClick={() => setDriverTipsModalOpen(true)}>
                                <InformationCircleIcon width={18} className="ml-10 text-black" />
                            </button>
                        </p>
                        {tipAdded && <p className="text-body-sm">${orderTip?.toFixed(2)}</p>}
                    </div>
                    {tipAdded && (
                        <p className="text-body-xs w-[calc(100%-100px)] text-grey">
                            You can adjust your tip amount up to 24 hours after delivery.
                        </p>
                    )}
                </div>
            )}
            {isMPlusInYourFace && !plusMembershipEligibleOrder && (
                <PlusMembershipSavingsBanner
                    intersectionRef={plusMembershipSavingsBannerRef}
                    savingsBannerInView={plusMembershipSavingsBannerInView}
                />
            )}
            <div className="mt-10 flex justify-between" data-testid="total">
                <p className="text-body-md font-grotesk-bold">TOTAL</p>
                <p className="text-body-md font-grotesk-bold">${total.toFixed(2)}</p>
            </div>
            {plusMembershipEligibleOrder && <PlusMembershipMemberBanner />}
            {!plusMembershipEligibleOrder && !isMPlusInYourFace && (
                <PlusMembershipProspectBanner intersectionRef={plusMembershipStaticBannerRef} />
            )}
            {cartNonMemberSavings > 0 && !plusMembershipEligibleOrder && (
                <div className="mt-10 flex items-center" data-testid="cart-non-member-savings">
                    <CurrencyDollarIcon className="h-15 w-15" />
                    <p className="text-body-sm ml-5">
                        <span className="text-brand-primary font-grotesk-bold">${cartNonMemberSavings.toFixed(2)}</span>{' '}
                        saved
                    </p>
                </div>
            )}
            {sumCreditBalance > 0 && (
                <div className="mt-5 flex items-center" data-testid="available-credits">
                    <BanknotesIcon className="h-15 w-15 shrink-0" />
                    <p className="text-body-sm ml-5">
                        <span className="text-brand-primary font-grotesk-bold">${sumCreditBalance.toFixed(2)}</span>{' '}
                        credits available. A max of ${MAX_CREDITS_PER_ORDER} in credits may be applied to any single
                        order.
                    </p>
                </div>
            )}
            {unmetReferralDiscount && (
                <div className="mt-5 flex items-start" data-testid="unmet-referral-discount">
                    <BanknotesIcon className="mt-[6px] h-15 w-15 shrink-0" />
                    <p className="text-body-sm ml-5">
                        <span className="text-brand-primary font-grotesk-bold">
                            ${unmetReferralDiscount.dollarDiscount?.toFixed(2)}
                        </span>{' '}
                        referral credits available to spend when your subtotal reaches{' '}
                        <span className="text-brand-primary font-grotesk-bold">
                            ${unmetReferralDiscount.thresholdDollars?.toFixed(2)}
                        </span>
                    </p>
                </div>
            )}
            {loyaltyEligible && estimatedLoyaltyPoints > 0 && (
                <div className="mt-5 flex items-center" data-testid="loyalty-points">
                    <StarIcon className="h-15 w-15" />
                    <p className="text-body-sm ml-5">
                        <span className="text-brand-primary font-grotesk-bold">{estimatedLoyaltyPoints}</span> points
                        earned
                    </p>
                </div>
            )}
            <DriverTipsInfoModal open={driverTipsModalOpen} setOpen={setDriverTipsModalOpen} />
            <CreditsInfoModal open={creditsInfoModalOpen} setOpen={setCreditsInfoModalOpen} />
            <EBTPaymentInfoModal open={ebtPaymentInfoModalOpen} setOpen={setEBTPaymentInfoModalOpen} />
            <DiscountInfoModal
                open={discountInfoModalOpen}
                discount={discountInfoModalDiscount}
                onClose={() => {
                    setDiscountInfoModalOpen(false);
                    setDiscountInfoModalDiscount(null);
                }}
            />

            <TaxAndFeesModal
                estimatedTax={{
                    display: true,
                    value: estimatedTax,
                }}
                handleClose={() => setTaxAndFeesModalOpen(false)}
                open={taxAndFeesModalOpen}
                operationsFee={{
                    display: showOperationsFee,
                    value: operationsFee,
                }}
            />
            {featureCanAddDriverTips && (
                <EditDriverTipModal
                    chargeID={chargeID}
                    driverTipsModal={editDriverTipsModalOpen}
                    orderTotal={total + orderTip}
                    orderSubtotal={subtotal}
                    closeEditDriverTipsModal={() => {
                        setEditDriverTipsModalOpen(false);
                    }}
                />
            )}
            {plusMembershipJoinModalOpen && (
                <JoinModal open={plusMembershipJoinModalOpen} onClose={() => setPlusMembershipJoinModalOpen(false)} />
            )}
        </div>
    );
}

export { CartPrices };
